import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowUpRight, BadgeIndianRupee, Calculator, HeartPulse, PlayCircle, Sparkles, X } from 'lucide-react';
import { PageHeader, StagePage } from 'src/stage/StageUi';
import { BILLING_HUB_URL, loadRateCard } from './pricingModel';
import SpendOverview from './SpendOverview';
import PricingStory from './PricingStory';
import PriceList from './PriceList';
import StayHealthy from './StayHealthy';
import useMetaUsage from './useMetaUsage';
import 'src/stage/stage.css';
import 'src/stage/wa-stage.css';
import './metaBilling.css';

// AUTO-WA-39: "Meta Billing" — what Meta charges for WhatsApp, why, and how to
// keep it low. Moved out of Settings so owners find it and learn from it.
export const TABS = [
  { id: 'overview', label: 'Your spend', icon: BadgeIndianRupee },
  { id: 'how-it-works', label: 'How pricing works', icon: PlayCircle },
  { id: 'prices', label: 'Prices & estimate', icon: Calculator },
  { id: 'healthy', label: 'Stay healthy', icon: HeartPulse },
];

const NOTICE_KEY = 'autoappy.whatsapp.metaBilling.oct2026Notice';

function ChangeNotice() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(NOTICE_KEY) === 'hidden'; } catch { return false; }
  });
  if (hidden) return null;
  const hide = () => {
    setHidden(true);
    try { localStorage.setItem(NOTICE_KEY, 'hidden'); } catch { /* the notice just comes back next time */ }
  };
  return (
    <aside className="mb-notice ws-rise" aria-label="New from 1 October 2026">
      <span className="mb-notice-icon" aria-hidden="true"><Sparkles size={17} /></span>
      <div className="ws-grow">
        <strong>New from 1 October 2026: normal replies are no longer always free.</strong>
        <p>Each WhatsApp number gets 1,000 free replies a month. After that, every delivered reply costs the utility rate (₹0.115 in India). Utility templates sent inside the 24-hour window are charged too.</p>
      </div>
      <Link className="ws-btn ws-btn--quiet ws-btn--sm" to="/meta-billing/how-it-works?slide=new-rules">See how it works</Link>
      <button type="button" className="ws-row-more" aria-label="Hide this notice" onClick={hide}><X size={14} /></button>
    </aside>
  );
}

export default function MetaBillingPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const usage = useMetaUsage(days);
  const [card, setCard] = useState({ loading: true, value: null, error: false });

  useEffect(() => {
    let active = true;
    loadRateCard()
      .then((value) => { if (active) setCard({ loading: false, value, error: false }); })
      .catch(() => { if (active) setCard({ loading: false, value: null, error: true }); });
    return () => { active = false; };
  }, []);

  if (!tab) return <Navigate to="/meta-billing/overview" replace />;
  const current = TABS.find((item) => item.id === tab);
  if (!current) return <Navigate to="/meta-billing/overview" replace />;

  const account = usage.data?.account;
  const context = { usage, card, account, days, setDays };

  return (
    <StagePage label="Meta Billing" className="mb-page">
      <PageHeader
        eyebrow="Account"
        title="Meta Billing"
        description="What Meta charges for your WhatsApp messages, why, and how to keep it low. Meta bills these charges to you directly; they are separate from your Auto Appy plan."
        actions={(
          <a className="ws-btn ws-btn--quiet" href={BILLING_HUB_URL} target="_blank" rel="noreferrer">
            Meta Billing Hub<ArrowUpRight size={14} />
          </a>
        )}
      />

      <nav className="mb-tabs" aria-label="Meta Billing sections">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} type="button" className={`mb-tab${id === tab ? ' is-active' : ''}`} aria-current={id === tab ? 'page' : undefined} onClick={() => navigate(`/meta-billing/${id}`)}>
            <Icon size={16} aria-hidden="true" />{label}
          </button>
        ))}
      </nav>

      {tab !== 'how-it-works' && <ChangeNotice />}

      {tab === 'overview' && <SpendOverview {...context} />}
      {tab === 'how-it-works' && <PricingStory {...context} />}
      {tab === 'prices' && <PriceList {...context} />}
      {tab === 'healthy' && <StayHealthy {...context} />}
    </StagePage>
  );
}
